import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { GetDriveAccessRequestsResponse } from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import api from '../../api';
import DriveAccessPrompt from './DriveAccessPrompt';
import {
  DriveAccessRequestData,
  isRequestAnswered,
  useGoogleDrivePicker,
} from './useGoogleDrivePicker';

/**
 * App-level bridge that handles Google Drive file access requests from the
 * agent.
 *
 * With the drive.file OAuth scope the backend can only see files it created
 * or files the user explicitly granted. When the agent needs a file it can't
 * see it records a request and fires a `google-drive-picker-request` Pusher
 * event; this listener shows a consent prompt explaining which file is needed
 * and why, and only opens Google's Picker when the user clicks "Choose file".
 *
 * Requests are also persisted, so on mount we fetch any that are still
 * outstanding — a request raised while the user was on WhatsApp (or simply not
 * in the app) is therefore still actionable when they next open it, rather
 * than being lost with the Pusher event.
 *
 * Mounted next to TFAPusherListener under PusherProvider so it works from
 * any route.
 */

interface GoogleDrivePickerRequestEvent extends DriveAccessRequestData {
  userId: string;
}

const GoogleDrivePickerListener: React.FC = () => {
  const pusherContext = usePusher(true);
  const { getAccessTokenSilently } = useAuth0();
  const userChannel = pusherContext?.userChannel;
  const [pendingRequest, setPendingRequest] =
    useState<DriveAccessRequestData | null>(null);
  const clearRequest = useCallback(() => setPendingRequest(null), []);
  const {
    opening,
    pickerVisible,
    pickerError,
    setPickerError,
    openPicker,
    cancelRequest,
  } = useGoogleDrivePicker({
    onPicked: clearRequest,
    onCancelled: clearRequest,
  });

  useEffect(() => {
    if (!userChannel) return;

    const handlePickerRequest = (data: GoogleDrivePickerRequestEvent) => {
      console.log('[DrivePicker] Picker request received:', data);
      setPickerError(null);
      setPendingRequest(data);
    };

    userChannel.bind('google-drive-picker-request', handlePickerRequest);
    return () => {
      userChannel.unbind('google-drive-picker-request', handlePickerRequest);
    };
  }, [userChannel, setPickerError]);

  // Outstanding requests recorded while the user was away from the app.
  useEffect(() => {
    // The deep link page owns the prompt for the request it was opened with.
    if (window.location.pathname.startsWith('/grant-access/')) return;
    let cancelled = false;
    const loadOutstanding = async () => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await api.get<GetDriveAccessRequestsResponse>(
          '/google-link/drive-access-requests',
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const oldest = (data.requests ?? []).find(
          (request) => !isRequestAnswered(request.id),
        );
        if (cancelled || !oldest) return;
        setPendingRequest(
          (current) =>
            current ?? {
              requestId: oldest.id,
              threadId: oldest.threadId,
              agentId: oldest.agentId,
              oauthId: oldest.oauthId,
              fileType: oldest.fileType,
              fileName: oldest.fileName ?? null,
            },
        );
      } catch (err) {
        console.error(
          '[DrivePicker] Failed to load outstanding access requests:',
          err,
        );
      }
    };
    void loadOutstanding();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const dismissRequest = useCallback(() => {
    if (pendingRequest) cancelRequest(pendingRequest);
    setPendingRequest(null);
    setPickerError(null);
  }, [pendingRequest, cancelRequest, setPickerError]);

  // Google's Picker owns the screen once it opens; leaving this modal up would
  // cover it and swallow taps.
  if (!pendingRequest || pickerVisible) return null;

  return (
    <DriveAccessPrompt
      request={pendingRequest}
      opening={opening}
      error={pickerError}
      onCancel={dismissRequest}
      onChoose={() => void openPicker(pendingRequest)}
    />
  );
};

export default GoogleDrivePickerListener;
